import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, ChevronLeft, Clock, Download, ExternalLink, FileX, Globe, RefreshCw, Send } from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { api } from '../../lib/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { QuoteVersion, getVersionExpirationStatus, formatExpiration } from '../../types/admin';

export function QuoteVersionViewPage() {
  const { quoteId, versionId } = useParams<{ quoteId: string; versionId: string }>();
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const orgId = activeOrg?.orgId ?? null;
  const clientQuotesUrl = `/moves/${quoteId}/client-quotes`;

  const [version, setVersion] = useState<QuoteVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState('');

  useEffect(() => {
    if (!orgId || !quoteId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ success: boolean; data: QuoteVersion[] }>(
          `/quotes/${orgId}/${quoteId}/versions`,
        );
        const found = res.data.find(v => v.id === versionId);
        if (!cancelled) {
          if (found) {
            setVersion(found);
          } else {
            setError('Quote version not found');
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load version');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, quoteId, versionId]);

  const onlineQuotesEnabled = activeOrg?.featureClientPortal !== false;

  async function handleSendEmail() {
    if (!orgId || !quoteId || !versionId || sending) return;
    setSending(true);
    setSendError('');
    setSendSuccess(false);
    try {
      await api.post(`/quotes/${orgId}/${quoteId}/versions/${versionId}/send-email`);
      setSendSuccess(true);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  }

  async function handleRegeneratePdf() {
    if (!orgId || !quoteId || !versionId || regenerating) return;
    setRegenerating(true);
    setRegenerateError('');
    try {
      const res = await api.post<{ success: boolean; data: { pdfUrl: string } }>(
        `/quotes/${orgId}/${quoteId}/versions/${versionId}/regenerate-pdf`,
      );
      setVersion(prev => prev ? { ...prev, pdfUrl: res.data.pdfUrl } : prev);
    } catch (err) {
      setRegenerateError(err instanceof Error ? err.message : 'Failed to regenerate PDF');
    } finally {
      setRegenerating(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !version) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-2 text-center">
        <p className="text-small text-rose font-semibold">{error || 'Version not found'}</p>
      </div>
    );
  }

  const pdfUrl = version.pdfUrl;

  return (
    <div className="px-4 pt-6 pb-10 md:mx-auto md:max-w-3xl md:px-6">
      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(clientQuotesUrl)}
        className="flex items-center gap-1 text-small text-slate hover:text-midnight mb-4
                   transition-colors -ml-1"
      >
        <ChevronLeft size={16} />
        Client Quotes
      </button>

      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="font-display text-h2 text-midnight truncate">
            {version.quoteVersionId}
          </h1>
          <p className="text-[13px] text-slate">
            Version {version.versionNumber} &middot; {version.quoteDate}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {version.portalUrl && onlineQuotesEnabled && (
            <a
              href={version.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 px-3 rounded-lg border border-stone text-[13px] font-medium
                         flex items-center gap-1.5 transition-colors
                         text-midnight hover:border-plum/40 hover:text-plum"
            >
              <ExternalLink size={14} />
              Online Quote
            </a>
          )}
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="h-9 w-9 rounded-lg border border-stone text-slate flex items-center justify-center
                         hover:border-plum hover:text-plum active:bg-mist
                         transition-colors duration-150"
              aria-label="Download PDF"
            >
              <Download size={16} />
            </a>
          )}
          {onlineQuotesEnabled && version.portalToken && (
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={sending || sendSuccess}
              className={`h-9 px-4 rounded-lg text-[13px] font-semibold
                         flex items-center gap-1.5 transition-colors
                         ${sendSuccess
                           ? 'bg-emerald-600 text-white'
                           : 'bg-plum text-white hover:bg-rose active:opacity-80'}
                         disabled:opacity-60`}
            >
              {sendSuccess ? (
                <>
                  <CheckCircle size={14} />
                  Sent
                </>
              ) : (
                <>
                  <Send size={14} />
                  {sending ? 'Sending...' : 'Send to Customer'}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Send result feedback */}
      {sendError && (
        <div className="flex items-center gap-2.5 p-3 mb-4 bg-rose/10 border border-rose/30 rounded-xl">
          <AlertTriangle size={16} className="shrink-0 text-rose" />
          <p className="text-[13px] text-rose font-medium">{sendError}</p>
        </div>
      )}
      {sendSuccess && (
        <div className="flex items-center gap-2.5 p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl">
          <CheckCircle size={16} className="shrink-0 text-emerald-600" />
          <p className="text-[13px] text-emerald-800">Quote email sent to customer successfully.</p>
        </div>
      )}

      {/* Expiration banner */}
      {(() => {
        const status = getVersionExpirationStatus(version);
        if (status === 'expired') return (
          <div className="flex items-center gap-2.5 p-3 mb-4 bg-rose/10 border border-rose/30 rounded-xl">
            <AlertTriangle size={16} className="shrink-0 text-rose" />
            <p className="text-[13px] text-rose font-medium">{formatExpiration(version)}</p>
          </div>
        );
        if (status === 'expiring_soon') return (
          <div className="flex items-center gap-2.5 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl">
            <Clock size={16} className="shrink-0 text-amber-600" />
            <p className="text-[13px] text-amber-800 font-medium">{formatExpiration(version)}</p>
          </div>
        );
        if (status === 'active') return (
          <div className="flex items-center gap-2.5 p-3 mb-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <Clock size={16} className="shrink-0 text-emerald-600" />
            <p className="text-[13px] text-emerald-800">{formatExpiration(version)}</p>
          </div>
        );
        return null;
      })()}

      {/* PDF viewer or no-PDF message */}
      {pdfUrl ? (
        <>
          <div className="rounded-xl border border-stone overflow-hidden bg-mist">
            <iframe
              src={`${pdfUrl}#view=FitH`}
              title="Quote PDF"
              referrerPolicy="no-referrer"
              className="w-full h-[calc(100vh-220px)] min-h-[600px] md:min-h-[800px]"
            />
          </div>
          {/* No `sandbox` on the iframe: the HTML sandbox flag disables plugins, and Chrome / Edge render
              PDFs with their built-in plugin, so a sandboxed frame shows a blank page. */}
          {/* Show regenerate option below iframe in case the PDF is expired but we can't detect it cross-origin */}
          <div className="flex items-center justify-center gap-3 mt-3">
            {regenerateError && (
              <p className="text-[13px] text-rose font-medium">{regenerateError}</p>
            )}
            <button
              type="button"
              onClick={handleRegeneratePdf}
              disabled={regenerating}
              className="text-[13px] text-slate hover:text-plum flex items-center gap-1.5 transition-colors disabled:opacity-60"
            >
              <RefreshCw size={13} className={regenerating ? 'animate-spin' : ''} />
              {regenerating ? 'Regenerating PDF...' : 'PDF not loading? Regenerate'}
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-4 text-center
                        rounded-xl border border-stone bg-mist">
          <FileX size={32} className="text-slate" />
          <div className="flex flex-col gap-1.5">
            <p className="text-small font-medium text-midnight">No PDF was generated for this quote</p>
            <p className="text-[13px] text-slate leading-snug max-w-xs">
              PDF generation may not have been configured when this version was created.
              You can generate one now.
            </p>
          </div>
          {regenerateError && (
            <p className="text-[13px] text-rose font-medium">{regenerateError}</p>
          )}
          <button
            type="button"
            onClick={handleRegeneratePdf}
            disabled={regenerating}
            className="h-9 px-4 rounded-lg bg-plum text-white text-[13px] font-semibold
                       flex items-center gap-1.5 transition-colors
                       hover:bg-rose active:opacity-80 disabled:opacity-60"
          >
            <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
            {regenerating ? 'Regenerating...' : 'Regenerate PDF'}
          </button>
          {onlineQuotesEnabled && version.portalUrl && (
            <div className="flex items-center gap-2 mt-2 px-4 py-2.5 bg-plum/5 border border-plum/20 rounded-lg">
              <Globe size={14} className="shrink-0 text-plum" />
              <p className="text-[13px] text-midnight leading-snug">
                This quote can still be viewed online via the{' '}
                <a
                  href={version.portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-plum hover:underline"
                >
                  Online Quote
                </a>{' '}
                link.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
